import React, { useContext, useRef } from 'react';
import { observer } from 'mobx-react';
import { useIntl } from 'react-intl';
import { Route, Switch, generatePath, Redirect } from 'react-router-dom';
import Overview from './Overview';

import {
  ROUTE_PERSONS_ENTITY,
  ROUTE_PERSONS_ENTITY_PERSON,
  ROUTE_PERSONS_ENTITY_PERSON_GLOBAL,
  ROUTE_PERSONS_ENTITY_PERSON_GLOBAL_EDIT,
  ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION,
  ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS,
  ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA_EDIT,
  ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA,
  ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_REPORT_TESTDATA,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_ENTITY,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_RETEST,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL_EDIT,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_REPORT_TESTDATA,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS_TESTDATA,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS_TESTDATA_EDIT,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL,
  ROUTE_PERSONS_ENTITY_PERSON_VIDEOS,
  ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS,
  ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM,
  ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM_FEEDBACK,
  ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM_EXERCISE,
  ROUTE_PERSONS_ENTITY_PERSON_TRAININGS,
  ROUTE_PERSONS_ENTITY_PERSON_GROUPS,
  ROUTE_PERSONS_ENTITY_PERSON_DOCS,
  ROUTE_PERSONS_ENTITY_PERSON_EDIT,
  ROUTE_SESSIONS_ENTITY,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL_EDIT,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL_EDIT,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_DETAIL,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_DETAIL_EDIT,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_REPORT,
  ROUTE_PERSONS,
  ROUTE_PERSONS_ENTITY_PERSON_INPUT_DATA
} from 'routes/RouteList';

import Person from 'containers/pages/persons/Person';
import PersonSession from 'containers/pages/persons/PersonSession';
import messages from 'messages';
import Injury from 'containers/pages/rehab/injury/Injury';
import Screening from 'containers/pages/rehab/screening/Screening';
import { StoreContext } from 'index';
import ScheduleReTestScreening from 'containers/pages/rehab/screening/ScheduleReTestScreening';
import { MedicalContextProvider } from 'contexts/MedicalContext';
import { SessionContextProvider } from 'contexts/SessionContext';
import { PreviewModalProvider } from 'contexts/PreviewModalContext';
import { EditableContextProvider } from 'contexts/EditableContext';
import PersonProgram from 'containers/pages/persons/PersonProgram';
import PersonProgramExercise from 'containers/pages/persons/PersonProgramExercise';
import NoAccessPanel from 'containers/pages/auth/NoAccessPanel';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import Illness from 'containers/pages/rehab/illness/Illness';
import Issue from 'containers/pages/rehab/issue/Issue';
import { TestDataProvider } from 'contexts/TestDataContext';
import {
  createEntityStore,
  EntityStoreProvider
} from 'containers/pages/persons/store/EntityStoreContext';
import ContextMenuProvider from 'contexts/ContextMenuProvider';
import { Panel } from 'components/panel';

const Persons = () => {
  const ability = useAbility(AbilityContext);
  const {
    authStore: {
      user,
      user: { rootEntityId }
    }
  } = useContext(StoreContext);
  const intl = useIntl();

  const entityStore = useRef(createEntityStore()).current;

  if (ability.cannot('read', 'Persons')) {
    return (
      <NoAccessPanel
        entityId={user.rootEntityId}
        emptyLabel={intl.formatMessage(messages.noAccessEmptyPanel)}
        redirectRoute={generatePath(ROUTE_SESSIONS_ENTITY, {
          entityId: user.rootEntityId
        })}
      />
    );
  }

  return (
    <EntityStoreProvider value={entityStore}>
      <MedicalContextProvider rootEntityId={rootEntityId}>
        <EditableContextProvider>
          <Switch>
            <Route
              exact
              path={[
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_REPORT,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_REPORT_TESTDATA,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS_TESTDATA,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS_TESTDATA_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_ENTITY,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_DETAIL,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_DETAIL_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS,
                ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM,
                ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM_FEEDBACK,
                ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM_EXERCISE,
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA,
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_REPORT_TESTDATA,
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS,
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION,
                ROUTE_PERSONS_ENTITY_PERSON_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_GLOBAL_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_GLOBAL,
                ROUTE_PERSONS_ENTITY_PERSON_INPUT_DATA,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL,
                ROUTE_PERSONS_ENTITY_PERSON_VIDEOS,
                ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS,
                ROUTE_PERSONS_ENTITY_PERSON_TRAININGS,
                ROUTE_PERSONS_ENTITY_PERSON_GROUPS,
                ROUTE_PERSONS_ENTITY_PERSON_DOCS,
                ROUTE_PERSONS_ENTITY_PERSON,
                ROUTE_PERSONS_ENTITY
              ]}
            >
              <ContextMenuProvider>
                <Overview />
              </ContextMenuProvider>
            </Route>
            <Route
              path={ROUTE_PERSONS}
              render={() => (
                <Redirect to={`${ROUTE_PERSONS}/${user.rootEntityId}`} />
              )}
            />
          </Switch>
          <Route exact path={[ROUTE_PERSONS_ENTITY]}>
            <Panel
              emptyState
              emptyLabel={intl.formatMessage(messages.panelSportersEmpty)}
            />
          </Route>
          <Switch>
            <Route
              exact
              path={[
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_REPORT,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_REPORT_TESTDATA,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS_TESTDATA,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS_TESTDATA_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_ENTITY,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_DETAIL,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_DETAIL_EDIT
              ]}
            >
              <ContextMenuProvider>
                <Injury />
              </ContextMenuProvider>
            </Route>

            <Route
              exact
              path={
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_RETEST
              }
            >
              <ScheduleReTestScreening />
            </Route>

            <Route
              exact
              path={[
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS,
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA,
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_REPORT_TESTDATA,
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS,
                ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM,
                ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM_FEEDBACK,
                ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM_EXERCISE
              ]}
            >
              <ContextMenuProvider>
                <Person />
              </ContextMenuProvider>
            </Route>
          </Switch>
          <Switch>
            <Route
              exact
              path={[
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS
              ]}
            >
              <ContextMenuProvider>
                <Injury />
              </ContextMenuProvider>
            </Route>
            <Route
              exact
              path={[
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ILLNESS_DETAIL_EDIT
              ]}
            >
              <ContextMenuProvider>
                <Illness />
              </ContextMenuProvider>
            </Route>
            <Route
              exact
              path={[
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_ISSUE_DETAIL_EDIT
              ]}
            >
              <ContextMenuProvider>
                <Issue />
              </ContextMenuProvider>
            </Route>
            <Route
              exact
              path={[
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_ENTITY,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_DETAIL,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_DETAIL_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_REPORT,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_REPORT_TESTDATA,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS_TESTDATA,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS_TESTDATA_EDIT
              ]}
            >
              <PreviewModalProvider entityId={user.rootEntityId}>
                <SessionContextProvider>
                  <TestDataProvider>
                    <ContextMenuProvider>
                      <Screening />
                    </ContextMenuProvider>
                  </TestDataProvider>
                </SessionContextProvider>
              </PreviewModalProvider>
            </Route>
            <Route
              exact
              path={
                ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM_EXERCISE
              }
            >
              <ContextMenuProvider>
                <PersonProgram />
              </ContextMenuProvider>
            </Route>
            <Route
              exact
              path={[
                ROUTE_PERSONS_ENTITY_PERSON,
                ROUTE_PERSONS_ENTITY_PERSON_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_GLOBAL,
                ROUTE_PERSONS_ENTITY_PERSON_GLOBAL_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION,
                ROUTE_PERSONS_ENTITY_PERSON_INPUT_DATA,
                ROUTE_PERSONS_ENTITY_PERSON_MEDICAL,
                ROUTE_PERSONS_ENTITY_PERSON_VIDEOS,
                ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS,
                ROUTE_PERSONS_ENTITY_PERSON_TRAININGS,
                ROUTE_PERSONS_ENTITY_PERSON_GROUPS,
                ROUTE_PERSONS_ENTITY_PERSON_DOCS
              ]}
            >
              <ContextMenuProvider>
                <Person />
              </ContextMenuProvider>
            </Route>
          </Switch>

          <Switch>
            <Route
              exact
              path={[
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION,
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS,
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA,
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA_EDIT,
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_REPORT_TESTDATA
              ]}
            >
              <SessionContextProvider>
                <PreviewModalProvider entityId={user.rootEntityId}>
                  <TestDataProvider>
                    <ContextMenuProvider>
                      <PersonSession />
                    </ContextMenuProvider>
                  </TestDataProvider>
                </PreviewModalProvider>
              </SessionContextProvider>
            </Route>
            <Route
              path={[
                ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM,
                ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM_FEEDBACK
              ]}
              exact
            >
              <ContextMenuProvider>
                <PersonProgram />
              </ContextMenuProvider>
            </Route>
            <Route
              path={[
                ROUTE_PERSONS_ENTITY_PERSON_PROGRAMS_ENTITY_PROGRAM_EXERCISE
              ]}
              exact
            >
              <ContextMenuProvider>
                <PersonProgramExercise />
              </ContextMenuProvider>
            </Route>
          </Switch>
        </EditableContextProvider>
      </MedicalContextProvider>
    </EntityStoreProvider>
  );
};

export default observer(Persons);
